import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import type { PeriodMonthColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager-month';
import HotelPricingAssistantTableTooltipsTooltipLeftToSell, {
  type LeftToSellTooltip,
} from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-left-to-sell';
import type { Period } from 'lumen/models';
import {
  isOccupancyLoading,
  type LeftToSellValues,
  resolveLeftToSellValues,
} from 'lumen/utils/pricing-assistant/left-to-sell-utils';
import { formatOverbookingValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

interface HotelPricingAssistantTableCellsTdOverbookingMonthSignature {
  Args: {
    column: PeriodMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdOverbookingMonth extends Component<HotelPricingAssistantTableCellsTdOverbookingMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get displayValue(): number | string {
    return formatOverbookingValue({
      intl: this.intl,
      value: this.values.overbooking,
    });
  }

  /* The overbooking needs both restrictions requests, as the Rate groups cell does.
     Rooms have no overbooking pushes yet. */
  get isLoading(): boolean {
    const { restrictionsRecommendationsRequestTask, restrictionsRequestTask } =
      this.pricingAssistantDataRequestService;

    return (
      restrictionsRequestTask.isRunning ||
      restrictionsRecommendationsRequestTask.isRunning ||
      (!this.isRoom && this.args.value.hasOverbookingActionInProgress)
    );
  }

  get isMissing(): boolean {
    return this.values.overbooking == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get isRoom(): boolean {
    return this.args.column.pmsRoomId !== undefined;
  }

  get showOverride(): boolean {
    return !this.isRoom && this.args.value.showOverbookingOverride;
  }

  /* The Left to sell cell's tooltip, once that cell shows a value: none while the
     availability loads, as the tooltip would read the previous one. */
  get tooltip(): LeftToSellTooltip | null {
    const { leftToSell, overbooking } = this.values;
    const isAvailabilityLoading = isOccupancyLoading(
      this.pricingAssistantDataRequestService,
      this.args.column.pmsRoomId,
    );

    if (isAvailabilityLoading || leftToSell == null || overbooking == null) {
      return null;
    }

    return { leftToSell, overbooking, roomName: this.args.column.roomName };
  }

  get values(): LeftToSellValues {
    return resolveLeftToSellValues(this.args.value, this.args.column.pmsRoomId);
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      {{! The marker takes the right gap that unmarked values keep, so values stay aligned.
          Focusable so keyboard users can open the tooltip }}
      <div
        class="flex items-center justify-end gap-100
          {{unless this.showOverride 'mr-500'}}
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id={{if
          this.isRoom
          "td-room-overbooking-month"
          "td-hotel-overbooking-month"
        }}
        tabindex={{if this.tooltip "0"}}
      >
        {{this.displayValue}}
        {{#if this.showOverride}}
          <OverrideIndicator
            @isDisabled={{this.isPast}}
            @variant="automation-off"
          />
        {{/if}}
        {{#if this.tooltip}}
          <HotelPricingAssistantTableTooltipsTooltipLeftToSell
            @date={{@value.fromDate}}
            @leftToSell={{this.tooltip.leftToSell}}
            @overbooking={{this.tooltip.overbooking}}
            @roomName={{this.tooltip.roomName}}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
